import { parse } from 'csv-parse/browser/esm/sync'
import { type AdminModel, notEmpty } from 'jms-domain'
import { useState } from 'react'
import { z } from 'zod'

import type { DomainEntityModification } from '@salsita-ai/platform-project-sdk/domain'
import { useLogger } from '@salsita-ai/platform-project-sdk/react'
import { LoadingButton } from '@salsita-ai/platform-project-sdk/ui'

import { adminInterface } from '../../systems/admin-interface'

const CsvRow = z.object({
  sku: z.string(),
  retail_price: z.coerce.number(),
  dealer_price: z.coerce.number(),
  // In inches
  package_width: z.coerce.number(),
  package_height: z.coerce.number(),
  package_depth: z.coerce.number(),
  // In pounds
  package_weight: z.coerce.number(),
})

type CsvRow = z.infer<typeof CsvRow>

export const ProductImportButton = () => {
  const { storeSystem, rootEntityId } = adminInterface.useDomain()
  const adminState = adminInterface.useEntityState(rootEntityId)
  const $pricingItems = adminState.$pricingItems

  const logger = useLogger()

  const [isImporting, setIsImporting] = useState(false)

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }

      const text = await file.text()
      const unsafeRecords: unknown[] = parse(text, { columns: true, skip_empty_lines: true })

      const records: CsvRow[] = unsafeRecords
        .map(record => {
          try {
            return CsvRow.parse(record)
          } catch (err) {
            alert(`Skipping invalid row in CSV file\n${JSON.stringify(record, null, 2)}`)
            logger.error('Error parsing CSV:', { err })
            return null
          }
        })
        .filter(notEmpty)

      const updates: Array<DomainEntityModification<AdminModel.PricingItem>> = []

      for (const record of records) {
        const {
          sku,
          retail_price: retailPrice,
          dealer_price: dealerPrice,
          package_width: packageWidth,
          package_height: packageHeight,
          package_depth: packageDepth,
          package_weight: packageWeight,
        } = record
        const item = $pricingItems.find(item => item.sku === sku)

        if (item) {
          const update = adminInterface.updates.DomainEntityModification<'Cabinet' | 'Appliance' | 'Part'>({
            entityId: item.id,
            stateDelta: {
              retailPrice,
              dealerPrice,
              packageWidth,
              packageHeight,
              packageDepth,
              packageWeight,
            },
            updateTag: 'PricingImport',
          })

          updates.push(update)
        }
      }

      storeSystem.stageAndCommit(updates)

      // reset input value
      event.target.value = ''
    } catch (err) {
      logger.error('Error importing pricing:', { err })
      alert('Could not parse provided CSV file')
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <>
      <label htmlFor="csv-upload" style={{ position: 'relative' }}>
        <input
          id="csv-upload"
          type="file"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: 'pointer',
            outline: 'none',
            zIndex: 1,
          }}
          onChange={handleFileUpload}
        />

        <LoadingButton variant="contained" component="span" busy={isImporting}>
          Upload File
        </LoadingButton>
      </label>
    </>
  )
}
